/**
 * TokenUsagePanel — Per-Replica Cost Visibility Tab (Tier 2)
 * 
 * Shows token consumption, estimated costs, and breakdowns by service/model.
 * Integrates into CognitiveLab as a tab.
 */
import React, { useState, useEffect, useCallback } from 'react';

interface UsageSummary {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCalls: number;
  avgInputTokens: number;
  avgOutputTokens: number;
  avgDurationMs: number;
  byService: Record<string, { calls: number; inputTokens: number; outputTokens: number }>;
  byModel: Record<string, { calls: number; inputTokens: number; outputTokens: number }>;
  periodStart: string;
  periodEnd: string;
}

interface Props {
  replicaId: string;
  getToken: () => Promise<string>;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const PERIOD_OPTIONS = [
  { label: '1h', hours: 1 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 },
];

const SERVICE_ICONS: Record<string, string> = {
  chat: '💬',
  workflow: '⚙️',
  classifier: '🏷️',
  pattern: '🔍',
  embedding: '🧮',
  consolidation: '📦',
};

const MODEL_COLORS: Record<string, string> = {
  'gpt-5.4-mini': '#22c55e',
  'text-embedding-3-small': '#6366f1',
};

// Estimate cost per token (early 2026 pricing)
function estimateCost(input: number, output: number, model: string): number {
  switch (model) {
    case 'gpt-5.4-mini':
      return (input * 0.75 + output * 4.5) / 1_000_000;
    case 'text-embedding-3-small':
      return (input * 0.02) / 1_000_000;
    default:
      return (input * 3 + output * 15) / 1_000_000;
  }
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export const TokenUsagePanel: React.FC<Props> = ({ replicaId, getToken }) => {
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(24);

  const loadUsage = useCallback(async (hours: number) => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/v1/usage/${replicaId}?hours=${hours}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setSummary(data.summary);
      }
    } catch (e) {
      console.error('Failed to load usage:', e);
    } finally {
      setLoading(false);
    }
  }, [replicaId, getToken]);

  useEffect(() => { loadUsage(selectedPeriod); }, [loadUsage, selectedPeriod]);

  // Calculate total estimated cost
  const totalEstimatedCost = summary
    ? Object.entries(summary.byModel).reduce((total, [model, data]) => {
        return total + estimateCost(data.inputTokens, data.outputTokens, model);
      }, 0)
    : 0;

  const totalTokens = (summary?.totalInputTokens || 0) + (summary?.totalOutputTokens || 0);

  // Max bar for proportional rendering
  const serviceEntries = summary ? Object.entries(summary.byService) : [];
  const maxServiceTokens = Math.max(...serviceEntries.map(([, d]) => d.inputTokens + d.outputTokens), 1);

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-4xl mx-auto space-y-5">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📊</span>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Token Usage</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Per-replica cost visibility & consumption analytics
              </p>
            </div>
          </div>

          {/* Period Selector */}
          <div className="flex gap-1 bg-slate-100 dark:bg-slate-800/60 rounded-lg p-0.5">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.hours}
                onClick={() => setSelectedPeriod(opt.hours)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  selectedPeriod === opt.hours
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !summary || summary.totalCalls === 0 ? (
          /* ── Empty State ── */
          <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-12 text-center">
            <span className="text-4xl block mb-3">📊</span>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">
              No usage data for this period
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Token tracking starts recording when you chat with your replica.
            </p>
          </div>
        ) : (
          <>
            {/* ── Key Metrics ── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Tokens', value: formatTokens(totalTokens), sub: `${formatTokens(summary.totalInputTokens)} in / ${formatTokens(summary.totalOutputTokens)} out`, icon: '🧮', color: '#6366f1' },
                { label: 'API Calls', value: summary.totalCalls.toLocaleString(), sub: `Avg ${Math.round(summary.avgDurationMs)}ms latency`, icon: '⚡', color: '#f59e0b' },
                { label: 'Est. Cost', value: `$${totalEstimatedCost.toFixed(4)}`, sub: 'Based on published pricing', icon: '💰', color: '#22c55e' },
                { label: 'Avg Tokens/Call', value: formatTokens(summary.avgInputTokens + summary.avgOutputTokens), sub: `${formatTokens(summary.avgInputTokens)} in / ${formatTokens(summary.avgOutputTokens)} out`, icon: '📐', color: '#ec4899' },
              ].map(metric => (
                <div
                  key={metric.label}
                  className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{metric.icon}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{metric.label}</span>
                  </div>
                  <span className="text-xl font-black block" style={{ color: metric.color }}>
                    {metric.value}
                  </span>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 block">
                    {metric.sub}
                  </span>
                </div>
              ))}
            </div>

            {/* ── By Service ── */}
            <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-5">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                By Service
              </h3>
              <div className="space-y-3">
                {serviceEntries
                  .sort(([, a], [, b]) => (b.inputTokens + b.outputTokens) - (a.inputTokens + a.outputTokens))
                  .map(([service, data]) => {
                    const total = data.inputTokens + data.outputTokens;
                    const pct = (total / maxServiceTokens) * 100;
                    const icon = SERVICE_ICONS[service] || '🔧';
                    return (
                      <div key={service}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{icon}</span>
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300 capitalize">{service}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                            <span>{data.calls} calls</span>
                            <span className="font-mono">{formatTokens(total)} tokens</span>
                          </div>
                        </div>
                        <div className="w-full h-2 bg-slate-200 dark:bg-slate-700/50 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-indigo-500 to-cyan-400 rounded-full transition-all duration-700"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* ── By Model ── */}
            <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl p-5">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                By Model
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(summary.byModel).map(([model, data]) => {
                  const color = MODEL_COLORS[model] || '#8b5cf6';
                  const cost = estimateCost(data.inputTokens, data.outputTokens, model);
                  return (
                    <div
                      key={model}
                      className="bg-slate-50 dark:bg-slate-700/30 rounded-xl p-4 border border-slate-100 dark:border-slate-700/30"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 font-mono">{model}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <span className="text-sm font-bold text-slate-900 dark:text-white block">{data.calls}</span>
                          <span className="text-[10px] text-slate-400">Calls</span>
                        </div>
                        <div>
                          <span className="text-sm font-bold text-slate-900 dark:text-white block">{formatTokens(data.inputTokens + data.outputTokens)}</span>
                          <span className="text-[10px] text-slate-400">Tokens</span>
                        </div>
                        <div>
                          <span className="text-sm font-bold block" style={{ color }}>
                            ${cost.toFixed(4)}
                          </span>
                          <span className="text-[10px] text-slate-400">Est. Cost</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TokenUsagePanel;
